import { dateToApiFormat } from '@/helpers/dateHelper';
import { getRequestStatus } from '@/helpers/requestStatusHelper';
import { RequestRow, RequestType } from '@/pages/team/teamMember/requests/RequestsTable.types';
import { useMyAvailabilityExceptionRequests } from '@/services/availability/useMyAvailabilityExceptionRequests';
import { useGetLeaveType } from '@/services/leaveManagement/LeaveTypesProvider';
import { useWorkingPatternRequests } from '@/services/workingPatterns/useWorkingPatternRequests';
import { useMemo } from 'react';

export type MyRequestRow = RequestRow & {
  startDate?: string;
  endDate?: string;
  title: string;
  denialReason?: string;
};

export function useMyRequestRows(userId: string): MyRequestRow[] {
  const { data } = useWorkingPatternRequests(userId);
  const { requests: availabilityExceptionRequests } = useMyAvailabilityExceptionRequests();
  const getLeaveType = useGetLeaveType();
  const today = dateToApiFormat(new Date());

  return useMemo<MyRequestRow[]>(() => {
    const patternRows = (data?.workingPatternRequestsByEmployeeId ?? []).flatMap((request): MyRequestRow[] => {
      if (!request) return [];
      return [
        {
          id: request.id,
          dateSubmitted: request.requestedOn,
          type: RequestType.ScheduleChange,
          title: RequestType.ScheduleChange,
          details: '',
          status: getRequestStatus(request.state),
          startDate: request.effectiveFrom ?? undefined,
          isPast: !!request.effectiveFrom && request.effectiveFrom < today,
          denialReason: request.rejectionReason
        }
      ];
    });

    const availabilityRows = (availabilityExceptionRequests ?? []).flatMap((request): MyRequestRow[] => {
      if (!request?.requestState) return [];

      const exception = request.exceptions?.[0];
      const leaveType = exception?.leaveType ? getLeaveType(exception.leaveType.id) : undefined;
      const type = exception?.leaveType ? RequestType.LeaveRequest : RequestType.AvailabilityChange;

      return [
        {
          id: request.id,
          dateSubmitted: request.requestedOn,
          type,
          title: leaveType?.properties.displayName ?? type,
          details: leaveType?.properties.displayName ?? '',
          status: getRequestStatus(request.requestState),
          startDate: request.start,
          endDate: request.end,
          isPast: request.end < today,
          denialReason: request.rejectionReason
        }
      ];
    });

    return [...patternRows, ...availabilityRows];
  }, [data, availabilityExceptionRequests, getLeaveType, today]);
}
